import { Controller } from '@hotwired/stimulus'

// The two-level checklist editor, shared by PRP program templates and audit templates.
// A checklist is sections, each with items. Both levels are clone-from-<template>:
// adding a section clones the section template (replacing the __SECTION_KEY__
// placeholder with a unique token so its items group under it); adding an item clones
// that section's own item template (replacing __ITEM_KEY__). The keys are opaque — the
// server reads the fields by DOM order — they exist only so Rails nests each item under
// the right section, and so a question's own inputs pair with it.
//
// Audit templates add WEIGHTS on top. A weight is relative — the score is
// earned/possible — so a bare number tells the author nothing: "5" is meaningless
// without the total, and every question at 5 is identical to every question at 1. So we
// recompute each question's SHARE of the score live, plus the running total, and warn
// when every weight is the same (an unweighted audit the author believes is weighted).
// These targets are optional: the PRP editor has no weights and simply never declares
// them, so `hasShareTarget` short-circuits the whole thing.
export default class extends Controller {
  static targets = ['sections', 'sectionTemplate', 'share', 'total', 'item', 'flatWarning']
  static values = { removeSectionConfirm: String }

  connect () {
    if (this.sectionsTarget.children.length === 0) this.addSection()
    this.recalculate()
  }

  addSection () {
    const html = this.sectionTemplateTarget.innerHTML.replaceAll('__SECTION_KEY__', this.uniqueKey('s'))
    this.sectionsTarget.insertAdjacentHTML('beforeend', html)
    this.addItemTo(this.sectionsTarget.lastElementChild) // open the new section with one item
    this.recalculate()
  }

  // Removing a section takes every question inside it with it — genuinely destructive,
  // and presented until now exactly like the cheap per-question ✕. Confirm it when
  // there is something to lose.
  removeSection (event) {
    const section = event.target.closest('[data-checklist-section]')
    if (!section) return

    const filled = [...section.querySelectorAll('[data-checklist-item] input[type="text"]')]
      .filter((input) => input.value.trim() !== '').length

    if (filled > 0 && this.hasRemoveSectionConfirmValue) {
      const message = this.removeSectionConfirmValue.replace('%{count}', String(filled))
      if (!window.confirm(message)) return
    }
    section.remove()
    this.recalculate()
  }

  addItem (event) {
    this.addItemTo(event.target.closest('[data-checklist-section]'))
    this.recalculate()
  }

  addItemTo (section) {
    const template = section.querySelector('[data-checklist-item-template]')
    const list = section.querySelector('[data-checklist-items]')
    list.insertAdjacentHTML('beforeend', template.innerHTML.replaceAll('__ITEM_KEY__', this.uniqueKey('i')))
  }

  removeItem (event) {
    event.target.closest('[data-checklist-item]')?.remove()
    this.recalculate()
  }

  recalculate () {
    if (!this.hasShareTarget) return // the PRP editor has no weights

    const weights = this.itemTargets.map((item) => this.weightOf(item))
    const total = weights.reduce((sum, weight) => sum + weight, 0)

    this.itemTargets.forEach((item, index) => {
      const share = item.querySelector('[data-compliance--checklist-builder-target="share"]')
      if (!share) return
      share.textContent = total > 0 ? `${Math.round((weights[index] / total) * 100)}%` : '—'
    })

    if (this.hasTotalTarget) this.totalTarget.textContent = String(total)

    if (this.hasFlatWarningTarget) {
      const distinct = new Set(weights.filter((weight) => weight > 0))
      this.flatWarningTarget.hidden = !(weights.length > 1 && distinct.size === 1)
    }
  }

  weightOf (item) {
    const checked = item.querySelector('input[type="radio"]:checked')
    return checked ? Number(checked.value) : 0
  }

  uniqueKey (prefix) {
    this.counter = (this.counter || 0) + 1
    return `${prefix}${Date.now()}${this.counter}`
  }
}
